import React from 'react';
import { View, TouchableOpacity, StyleSheet, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { LedgerAccountFilter } from '../../types/ledger';

interface AccountFilterModalProps {
  selectedFilter: LedgerAccountFilter;
  onFilterChange: (filter: LedgerAccountFilter) => void;
  visible: boolean;
  onClose: () => void;
}

const AccountFilterModal: React.FC<AccountFilterModalProps> = ({
  selectedFilter,
  onFilterChange,
  visible,
  onClose,
}) => {
  const { theme } = useTheme();

  const handleFilterSelect = (filter: LedgerAccountFilter) => {
    onFilterChange(filter);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
          <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}>
            <ThemedText style={[styles.modalTitle, { color: theme.text }]}>
              Filter Accounts
            </ThemedText>
            <TouchableOpacity onPress={onClose}>
              <Ionicons name="close" size={24} color={theme.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.modalBody}>
            <TouchableOpacity
              style={[
                styles.filterOption,
                { backgroundColor: selectedFilter === 'all' ? theme.backgroundTertiary : 'transparent' },
              ]}
              onPress={() => handleFilterSelect('all')}
              activeOpacity={0.7}
            >
              <View style={[styles.iconContainer, { backgroundColor: theme.accent + '20' }]}>
                <Ionicons name="list-outline" size={24} color={theme.accent} />
              </View>
              <View style={styles.filterTextContainer}>
                <ThemedText style={[styles.filterLabel, { color: theme.text }]}>
                  All Accounts
                </ThemedText>
                <ThemedText style={[styles.filterDescription, { color: theme.textSecondary }]}>
                  Show all ledger accounts
                </ThemedText>
              </View>
              {selectedFilter === 'all' && (
                <Ionicons name="checkmark-circle" size={24} color={theme.accent} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.filterOption,
                { backgroundColor: selectedFilter === 'bookmarked' ? theme.backgroundTertiary : 'transparent' },
              ]}
              onPress={() => handleFilterSelect('bookmarked')}
              activeOpacity={0.7}
            >
              <View style={[styles.iconContainer, { backgroundColor: '#FFB800' + '20' }]}>
                <Ionicons name="bookmark" size={24} color="#FFB800" />
              </View>
              <View style={styles.filterTextContainer}>
                <ThemedText style={[styles.filterLabel, { color: theme.text }]}>
                  Bookmarked
                </ThemedText>
                <ThemedText style={[styles.filterDescription, { color: theme.textSecondary }]}>
                  Show only bookmarked accounts
                </ThemedText>
              </View>
              {selectedFilter === 'bookmarked' && (
                <Ionicons name="checkmark-circle" size={24} color={theme.accent} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.filterOption,
                { backgroundColor: selectedFilter === 'shared' ? theme.backgroundTertiary : 'transparent' },
              ]}
              onPress={() => handleFilterSelect('shared')}
              activeOpacity={0.7}
            >
              <View style={[styles.iconContainer, { backgroundColor: '#2196F3' + '20' }]}>
                <Ionicons name="link" size={24} color="#2196F3" />
              </View>
              <View style={styles.filterTextContainer}>
                <ThemedText style={[styles.filterLabel, { color: theme.text }]}>
                  Shared Ledgers
                </ThemedText>
                <ThemedText style={[styles.filterDescription, { color: theme.textSecondary }]}>
                  Show only linked accounts
                </ThemedText>
              </View>
              {selectedFilter === 'shared' && (
                <Ionicons name="checkmark-circle" size={24} color={theme.accent} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.filterOption,
                { backgroundColor: selectedFilter === 'pending' ? theme.backgroundTertiary : 'transparent' },
              ]}
              onPress={() => handleFilterSelect('pending')}
              activeOpacity={0.7}
            >
              <View style={[styles.iconContainer, { backgroundColor: '#FF9800' + '20' }]}>
                <Ionicons name="mail-outline" size={24} color="#FF9800" />
              </View>
              <View style={styles.filterTextContainer}>
                <ThemedText style={[styles.filterLabel, { color: theme.text }]}>
                  Pending Invites
                </ThemedText>
                <ThemedText style={[styles.filterDescription, { color: theme.textSecondary }]}>
                  Show only pending invitations
                </ThemedText>
              </View>
              {selectedFilter === 'pending' && (
                <Ionicons name="checkmark-circle" size={24} color={theme.accent} />
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalBody: {
    paddingVertical: 20,
    paddingHorizontal: 16,
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  filterTextContainer: {
    flex: 1,
  },
  filterLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  filterDescription: {
    fontSize: 14,
  },
});

export default AccountFilterModal;
